import React, { useEffect } from "react";
import { useChooseSeats } from "./useChooseSeats";
import { useRepsProduct } from "../useRepsProduct";
import { selectIsInitialSeatCountHydrated } from "@/global/store/data/gameFilters";
import { useSelectActiveEvent } from "./useSelectActiveEvent";
import { useCartHelpers } from "../useCartHelpers";
import { v4 as uuid } from "uuid";
import { useAppStore } from "@/global/store";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { calcDefaultPurchaseSeatTab } from "../utils.purchaseSeats";
import { DynamicSkeleton } from "@/shared/base/components/DynamicSkeleton/DynamicSkeleton";
import { Grid, Box, Container, Stack, useTheme } from "@mui/material";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { CartTimeoutContainer } from "../timeout-worker/CartTimeoutContainer";
import { useTimeoutWorker } from "../timeout-worker/useTimeoutWorker";
import { useInventoryPreviewModal } from "../InventoryPreview/InventoryPreviewModal";
import { EventType } from "@/global/store/data/types";
import { usePacks } from "../Packs/usePacks";

// This is a higher order component that ensures the necessary data is pre-fetched
// before rendering the ChooseSeats component.
// mimicking suspense like behavior
export function withChooseSeatsPrefetch(Component: React.ComponentType<any>) {
  return function WrappedComponent(props: any) {
    const router = useRouterExtended();
    const teamConfig = useTeamConfig();
    const { handleGetInventoryFilters } = useChooseSeats();
    const {
      cancelCartByEvent,
      switchPurchaseSeatTab,
      removeCartEventFromCartState,
      removeCartProductsFromCartState,
    } = useCartHelpers();
    const isInitialSeatCountHydrated = useAppStore(selectIsInitialSeatCountHydrated); // prettier-ignore
    const { activeEvent, cartEventActive, urlActiveEventCode } =
      useSelectActiveEvent();
    const { isTimeoutEnabled } = useTimeoutWorker();
    const inventoryPreviewModal = useInventoryPreviewModal();
    const urlEventCodes = router?.query?.eventCodes as string;
    const urlPackageId = Number(router.query.packageId) as number;
    const [isFiltersLoaded, setIsFiltersLoaded] = React.useState(false); // prettier-ignore
    const { handleAuthWithEventCodes, handleLoadEvents, handleGetPackages } = useChooseSeats(); // prettier-ignore
    const { states: repsStates, handlers: repsHandlers } = useRepsProduct();
    const [renderKey, setRenderKey] = React.useState(uuid());
    const { handleCheckPackDynamicAccountGroup } = usePacks();

    const loadInventoryFilters = async (event: EventType) => {
      await handleGetInventoryFilters(event);
      setIsFiltersLoaded(true);
    };

    // prefetching necessary data
    useEffect(() => {
      (async () => {
        if (!isInitialSeatCountHydrated) return;

        // Handle auth callback with event codes
        await handleAuthWithEventCodes();

        handleCheckPackDynamicAccountGroup();

        // Handle getting events
        await handleLoadEvents();

        // Handle getting packages
        await handleGetPackages();

        // checking if reps product
        if (repsStates.isRepProduct) repsHandlers.checkIsRepProduct();
      })();
    }, [isInitialSeatCountHydrated]);

    // cancelling cart by event if it's held in cart
    React.useEffect(() => {
      (async () => {
        // loading the filters
        if (activeEvent) await loadInventoryFilters(activeEvent);

        // ----------clean ups-----------------
        // if preview modal is open then prevent initial cleanup
        if (inventoryPreviewModal.isOpen) return;
        // removing order level perk from cart
        removeCartEventFromCartState("order-level");
        // cancelling cart by event
        await cancelCartByEvent({ cartEvent: cartEventActive });
        removeCartProductsFromCartState(cartEventActive);
        setRenderKey(uuid());
      })();
    }, [activeEvent]);

    // switching to default tab if no event is active or nothing related to event is in url
    React.useEffect(() => {
      if (!activeEvent && !urlActiveEventCode && !urlPackageId) {
        if (urlEventCodes) return;
        const defaultTab = calcDefaultPurchaseSeatTab(teamConfig);
        switchPurchaseSeatTab(defaultTab);
      }
    }, [activeEvent, urlActiveEventCode, urlEventCodes, urlPackageId]);

    return (
      <>
        {isTimeoutEnabled ? <CartTimeoutContainer /> : null}

        {activeEvent && isInitialSeatCountHydrated && isFiltersLoaded ? (
          <Component key={renderKey} {...props} />
        ) : (
          <PrefetchSkeleton />
        )}
      </>
    );
  };
}

const PrefetchSkeleton = () => {
  const { md: mdDown, lg: lgDown } = useCssMediaQuery("down");
  const theme = useTheme();

  return (
    <Container maxWidth="xl">
      <Box>
        <Box
          borderTop={`1px solid ${theme.palette.divider}`}
          borderBottom={`1px solid ${theme.palette.divider}`}
          py={1}
        >
          <Grid container spacing={2}>
            {Array.from({ length: mdDown ? 1 : lgDown ? 2 : 4 })
              .fill(1)
              .map((itm, idx) => {
                return (
                  <Grid key={idx} item xs={12} sm={12} md={6} lg={3}>
                    <DynamicSkeleton
                      rowCount={1}
                      spacing={2}
                      widths={["100%"]}
                      heights={["18px"]}
                    />
                  </Grid>
                );
              })}
          </Grid>
        </Box>

        <Stack
          direction={mdDown ? "column" : "row"}
          spacing={mdDown ? 4 : 2}
          mt={2}
          width={"100%"}
          pb={2}
        >
          <Box width={"100%"}>
            <DynamicSkeleton
              rowCount={1}
              spacing={2}
              widths={["100%"]}
              heights={[mdDown ? "200px" : "400px"]}
            />
          </Box>

          <Stack width={"100%"} spacing={3}>
            {Array.from({ length: 4 })
              .fill(1)
              .map((itm, idx) => {
                return (
                  <Grid key={idx} item xs={12} sm={12} md={6} lg={3}>
                    <DynamicSkeleton
                      rowCount={3}
                      spacing={2}
                      widths={["100%", "80%", "60%"]}
                      heights={["14px", "14px", "14px"]}
                    />
                  </Grid>
                );
              })}
          </Stack>
        </Stack>
      </Box>
    </Container>
  );
};
